"use client";

import {
  AlertCircle,
  BookOpen,
  ChevronDown,
  GraduationCap,
  Info,
  Plus,
  ShoppingCart,
  X,
} from "lucide-react";
import { SelectOptionsType } from "@/types/dashboard.type";
import provincesData from "./data/provinces.json";
import countiesData from "./data/counties.json";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { kangoroSimple, samourSimple } from "@/assets/bebras";
import products from "./data/classes-quizes.json";
import { SignupFormValues, signupSchema } from "./schemas/signup";
import { submitKangarooRegistration } from "@/actions/submitKangarooRegistration";
import { useCart } from "@/context/CartContext";
import { getCartData } from "@/core/apiCalls/cart";
import APP_ROUTES from "@/constants/paths";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { PrepVideo, prepVideos } from "./data/prep-videos";

const ALWAYS_VISIBLE_VIDEO: PrepVideo = {
  title: "جلسه کلاس آمادگی",
  teacher: "",
  gradeLabel: "جلسات کلاس آمادگی",
  grades: [],
  embed: "https://www.aparat.com/video/video/embed/videohash/yhd9i21/vt/frame",
  cover: "",
};

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Province {
  id: number;
  name: string;
  slug: string;
  tel_prefix: string;
}

interface County {
  id: number;
  name: string;
  slug: string;
  province_id: number;
}

const provinces: Province[] = provincesData;
const counties: County[] = countiesData;

type Step = "selection" | "registration";

const gradeOptions: SelectOptionsType[] = [
  { id: "1", name: "اول - ابتدایی" },
  { id: "2", name: "دوم - ابتدایی" },
  { id: "3", name: "سوم - ابتدایی" },
  { id: "4", name: "چهارم - ابتدایی" },
  { id: "5", name: "پنجم - ابتدایی" },
  { id: "6", name: "ششم - ابتدایی" },
  { id: "7", name: "هفتم - متوسطه اول" },
  { id: "8", name: "هشتم - متوسطه اول" },
  { id: "9", name: "نهم - متوسطه اول" },
  { id: "10", name: "دهم - متوسطه دوم" },
  { id: "11", name: "یازدهم - متوسطه دوم" },
  { id: "12", name: "دوازدهم - متوسطه دوم" },
];

interface SpefSpefQuiz {
  id: string;
  title: string;
  price: number;
  productId: string;
}

interface SpefQuiz {
  "kangaroo-online": SpefSpefQuiz;
  "kangaroo-onsite": SpefSpefQuiz;
  "bebras-online": SpefSpefQuiz;
  "combo-online": SpefSpefQuiz;
  "combo-onsite": SpefSpefQuiz;
}

interface SpefClass {
  title: string;
  sessions: string;
  productId: string;
  price: number;
}

interface ClassesnQuizzesGrade {
  grade: number;
  gradeLabel: string;
  prepClass: SpefClass | null;
}

interface ClassesnQuizzesData {
  quizzes: SpefQuiz;
  grades: Record<string, ClassesnQuizzesGrade>;
}

const optionsCQ: ClassesnQuizzesData = products;

export default function FormModal({ isOpen, onClose }: FormModalProps) {
  const [step, setStep] = useState<Step>("selection");

  const [grade, setGrade] = useState("");
  const [wantsQuiz, setWantsQuiz] = useState(true);
  const [selectedQuiz, setSelectedQuiz] = useState<string>("combo-onsite");
  const [wantsClass, setWantsClass] = useState(true);

  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedCounty, setSelectedCounty] = useState("");

  const availableCounties = counties.filter(
    (c) => c.province_id === Number(selectedProvince)
  );

  const handleProvinceChange = (val: string) => {
    setSelectedProvince(val);
    setSelectedCounty("");
  };

  const handleClose = () => {
    onClose();
    setStep("selection");
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4 backdrop-brightness-75 md:backdrop-brightness-75 backdrop-blur-xs md:backdrop-blur-xs bg-white md:bg-transparent pt-20 md:pt-0"
      onClick={handleClose}
    >
      <div
        className="
    w-full
    md:max-w-3xl
    bg-white
    shadow-4xl
    h-[90vh] sm:h-auto md:max-h-[90vh]
    flex flex-col
    overflow-hidden
    rounded-t-3xl
    md:rounded-3xl
    mt-5 md:mt-0
  "
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-1 overflow-y-auto overscroll-contain p-4">
          {step === "selection" ? (
            <ClassQuizSelectionStep
              onClose={handleClose}
              onContinue={() => setStep("registration")}
              grade={grade}
              setGrade={setGrade}
              wantsQuiz={wantsQuiz}
              setWantsQuiz={setWantsQuiz}
              selectedQuiz={selectedQuiz}
              setSelectedQuiz={setSelectedQuiz}
              wantsClass={wantsClass}
              setWantsClass={setWantsClass}
            />
          ) : (
            <RegistrationFormStep
              onClose={handleClose}
              onBack={() => setStep("selection")}
              grade={grade}
              wantsQuiz={wantsQuiz}
              wantsClass={wantsClass}
              selectedQuiz={selectedQuiz}
              selectedProvince={selectedProvince}
              selectedCounty={selectedCounty}
              availableCounties={availableCounties}
              handleProvinceChange={handleProvinceChange}
              setSelectedCounty={setSelectedCounty}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function InitialQuizRow() {
  return (
    <div className="rounded-2xl p-2.5 py-4">
      <label
        className={`rounded-2xl flex items-center justify-between gap-4 p-4 transition-all duration-200 border border-BrightGray opacity-50 cursor-not-allowed`}
      >
        <div className="flex items-center gap-3">
          <span className="text-[15px] text-right">
            لطفا مقطع را انتخاب کنید.
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[15px] font-bold whitespace-nowrap">
            <span className="font-normal text-sm"></span>
          </span>
          <input
            type="radio"
            name="quiz-option"
            className="appearance-none w-7 h-7 rounded-full border-7 border-gray-300 disabled:border-gray-200 disabled:bg-gray-100 cursor-pointer disabled:cursor-not-allowed transition-colors duration-150"
          />
        </div>
      </label>
    </div>
  );
}

/* step1 — Class & Quiz Selection*/

function ClassQuizSelectionStep({
  onClose,
  onContinue,
  grade,
  setGrade,
  wantsQuiz,
  setWantsQuiz,
  selectedQuiz,
  setSelectedQuiz,
  wantsClass,
  setWantsClass,
}: {
  onClose: () => void;
  onContinue: () => void;
  grade: string;
  setGrade: (v: string) => void;
  wantsQuiz: boolean;
  setWantsQuiz: (v: boolean) => void;
  selectedQuiz: string;
  setSelectedQuiz: (v: string) => void;
  wantsClass: boolean;
  setWantsClass: (v: boolean) => void;
}) {
  const formatPrice = (price: number) => {
    return (price * 1000).toLocaleString("fa-IR");
  };
  const canContinue =
    (wantsQuiz || wantsClass) && (!wantsQuiz || !!selectedQuiz) && !!grade;

  const gradeData = optionsCQ.grades[grade];
  const quizzes = optionsCQ.quizzes;

  const hasPreparationClass =
    !!gradeData?.prepClass && grade !== "11" && grade !== "12";

  const classPriceForDisplayNum =
    hasPreparationClass && wantsClass ? gradeData.prepClass!.price * 1000 : 0;

  const quizPriceForDisplayNum =
    gradeData && wantsQuiz
      ? quizzes[selectedQuiz as keyof SpefQuiz].price * 1000
      : 0;

  return (
    <>
      <div className="mb-4 md:mx-5 flex items-center justify-between p-5">
        <X
          onClick={onClose}
          aria-label="Close"
          className="text-gray-600 hover:text-black cursor-pointer transition-colors duration-200 sm:w-10 w-5"
        />
        <h1 className="font-semibold sm:text-2xl text-[17px] text-gray-900">
          انتخاب آزمون و کلاس آمادگی
        </h1>
      </div>

      <h4
        style={{ direction: "rtl" }}
        className="flex font-light mx-2 md:mx-5 text-black/50 sm:gap-2.5 gap-1.5 sm:items-center items-start sm:text-md text-[12px]"
      >
        <Info className="sm:w-7 w-8" />
        نوع آزمون موردنظر خود را انتخاب کنید و مشخص نمایید آیا کلاس آمادگی آزمون
        را نیز میخواهید یا خیر.
      </h4>

      <div
        style={{ direction: "rtl" }}
        className="flex items-center justify-between mx-2 md:mx-5 mt-7 sm:mb-10 mb-5 gap-4 bg-gray-100 rounded-xl sm:p-3 p-2"
      >
        <label
          htmlFor="stepGrade"
          className="whitespace-nowrap sm:text-md text-sm"
        >
          انتخاب پایه تحصیلی:
        </label>
        <div className="flex-1">
          <SelectField
            id="stepGrade"
            placeholder="انتخاب کنید"
            options={gradeOptions}
            value={grade}
            onChange={setGrade}
            className="w-full sm:text-md text-[13px] sm:p-4 p-2 sm:rounded-2xl rounded-lg border border-LightGray bg-white sm:px-4 px-2 focus:outline-0 text-black text-right invalid:text-gray-500 cursor-pointer disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 [&>select]:appearance-none [&>select]:bg-none"
          />
        </div>
      </div>

      <div style={{ direction: "rtl" }} className="mx-1 md:mx-5">
        {!grade ? (
          <div className="rounded-2xl border border-LightGray p-8 text-center text-gray-500">
            لطفا مقطع را انتخاب کنید
          </div>
        ) : (
          <div>
            <div className="flex sm:flex-row sm:gap-0 gap-5 flex-col items-center justify-between mb-6 sm:mx-3 sm:bg-[#FFFFFF] bg-[#F5F7FF] sm:border-none border border-[#D8E3FE] rounded-2xl sm:p-0 p-3 ">
              <span className="flex items-center gap-2 sm:text-md text-[14px]">
                <GraduationCap strokeWidth={2} className="text-blue-600" />
                نوع آزمون
              </span>

              <BinaryToggle
                disabled={!gradeData}
                trueLabel="در آزمون شرکت میکنم"
                falseLabel="در آزمون شرکت نمیکنم"
                value={wantsQuiz}
                onChange={setWantsQuiz}
              />
            </div>

            <div className="rounded-2xl border border-LightGray overflow-scroll">
              {gradeData ? (
                Object.entries(quizzes).map(([id, quiz]) => (
                  <QuizOptionRow
                    key={id}
                    option={quiz}
                    disabled={!wantsQuiz}
                    selected={selectedQuiz === quiz.id}
                    onSelect={setSelectedQuiz}
                    className=""
                  />
                ))
              ) : (
                <InitialQuizRow />
              )}
            </div>
          </div>
        )}
      </div>

      <div
        style={{ direction: "rtl" }}
        className={`mx-2 md:mx-5 sm:mt-15 mt-8 ${!gradeData ? "opacity-50" : ""}`}
      >
        <div
          style={{ direction: "rtl" }}
          className={`mx-2 md:mx-5 sm:mt-15 mt-8 ${
            !gradeData ? "opacity-50" : ""
          }`}
        >
          {hasPreparationClass && (
            <div className="flex sm:flex-row sm:gap-0 gap-5 flex-col items-center justify-between mb-6 sm:mx-3 sm:bg-[#FFFFFF] bg-[#F5F7FF] sm:border-none border border-[#D8E3FE] rounded-2xl sm:p-0 p-2">
              <span className="flex items-center gap-2 sm:text-md text-[14px]">
                <BookOpen className="text-blue-600" />
                کلاس آمادگی آزمون
              </span>

              <BinaryToggle
                disabled={!gradeData}
                trueLabel="میخواهم در کلاس شرکت کنم"
                falseLabel="در کلاس شرکت نمی‌کنم"
                value={wantsClass}
                onChange={setWantsClass}
              />
            </div>
          )}

          <div
            className={`mt-10 flex flex-col rounded-2xl p-6 text-center transition-all duration-300 ${
              wantsClass && hasPreparationClass
                ? "bg-linear-to-l from-blue-900 to-blue-600 text-white"
                : "bg-blue-50 text-blue-200"
            }`}
          >
            <PrepVideoSlider
              grade={grade}
              enabled={hasPreparationClass && wantsClass}
              info={
                <>
                  <div className="text-lg font-bold">
                    {hasPreparationClass ? gradeData.prepClass?.sessions : ""}
                  </div>

                  <div className="mt-3 text-2xl font-bold">
                    {hasPreparationClass
                      ? formatPrice(gradeData.prepClass!.price)
                      : ""}
                    {hasPreparationClass && (
                      <span className="text-xl font-normal">&nbsp;تومان</span>
                    )}
                  </div>

                  {hasPreparationClass && (
                    <div className="flex items-start gap-2 text-xs opacity-80 sm:mt-3 sm:items-center">
                      <Info size={14} />
                      کلاس آمادگی آزمون نیازمند تکمیل فرم اطلاعات دانش‌آموز
                      نمی‌باشد.
                    </div>
                  )}
                </>
              }
            />
          </div>

          {gradeData && (wantsClass || wantsQuiz) && (
            <div className="rounded-2xl sm:mt-10 mt-8 p-5 flex flex-row items-center justify-between border-2 border-yellow-300 text-black md:max-h-[10vh] transition-all duration-300">
              <div className="sm:text-xl text-sm font-bold">
                <div className="flex gap-3">
                  <span className="text-gray-500 font-medium">
                    مجموع پرداخت :
                  </span>
                  {(
                    classPriceForDisplayNum + quizPriceForDisplayNum
                  ).toLocaleString("fa-IR")}{" "}
                  تومان
                </div>
              </div>

              <ShoppingCart className="text-blue-600 sm:w-8 sm:h-8" />
            </div>
          )}
        </div>
      </div>

      <div className="w-full md:w-1/2 flex items-center gap-4 mt-10">
        <div className="flex gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="
        border border-red-500
        text-red-500
        rounded-xl
        px-5 py-2.5
        sm:text-[17px]
        text-sm
        whitespace-nowrap
        cursor-pointer
        transition-all duration-200
        hover:bg-red-500 hover:text-white
      "
          >
            انصراف
          </button>

          <button
            type="button"
            onClick={onContinue}
            disabled={!canContinue || !gradeData}
            className="
        bg-blue-600
        text-white
        rounded-xl
        px-5 py-2.5
        sm:text-[17px]
        text-sm
        whitespace-nowrap
        cursor-pointer
        transition-all duration-200
        hover:bg-blue-700
        disabled:opacity-40
        disabled:cursor-not-allowed
      "
          >
            ادامه
          </button>
        </div>
        {!canContinue && grade && !wantsQuiz && !wantsClass && (
          <p
            style={{ direction: "rtl" }}
            className="
        text-red-500
        text-sm
        leading-6
        whitespace-nowrap
        text-right
      "
          >
            لطفاً حداقل یکی از آزمون یا کلاس آمادگی را انتخاب کنید.
          </p>
        )}
      </div>
    </>
  );
}

function BinaryToggle({
  trueLabel,
  falseLabel,
  value,
  disabled,
  onChange,
}: {
  trueLabel: string;
  falseLabel: string;
  value: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center sm:gap-6 gap-2">
      <label className="flex items-center gap-2 cursor-pointer sm:text-sm text-xs">
        {trueLabel}
        <input
          type="radio"
          checked={value}
          disabled={disabled}
          onChange={() => onChange(true)}
          className="appearance-none w-5 h-5 rounded-full border-5 border-gray-300 checked:border-blue-600 disabled:border-gray-200 disabled:bg-gray-100 cursor-pointer disabled:cursor-not-allowed transition-colors duration-150"
        />
      </label>
      <label className="flex items-center gap-2 cursor-pointer sm:text-sm text-xs">
        {falseLabel}
        <input
          type="radio"
          checked={!value}
          disabled={disabled}
          onChange={() => onChange(false)}
          className="appearance-none w-5 h-5 rounded-full border-5 border-gray-300 checked:border-red-600 disabled:border-gray-200 disabled:bg-gray-100 cursor-pointer disabled:cursor-not-allowed transition-colors duration-150"
        />
      </label>
    </div>
  );
}

function PrepVideoSlider({
  grade,
  enabled,
  info,
}: {
  grade: string;
  enabled: boolean;
  info: React.ReactNode;
}) {
  const videos = prepVideos.filter((v) => v.grades.includes(Number(grade)));

  const allVideos = [ALWAYS_VISIBLE_VIDEO, ...videos];

  const [activeEmbed, setActiveEmbed] = useState<string>(
    ALWAYS_VISIBLE_VIDEO.embed
  );

  useEffect(() => {
    setActiveEmbed(ALWAYS_VISIBLE_VIDEO.embed);
  }, [grade]);

  const active =
    allVideos.find((video) => video.embed === activeEmbed) ??
    ALWAYS_VISIBLE_VIDEO;

  const showVideos = enabled && videos.length > 0;

  return (
    <div
      className="flex flex-col gap-5 lg:flex-row lg:items-start"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="min-w-0 flex-1">
        <div className="text-right">{info}</div>

        <div className="mt-4 aspect-video w-full overflow-hidden rounded-xl border border-white/20 bg-black shadow-lg">
          <iframe
            key={active.embed}
            className="h-full w-full"
            src={active.embed}
            title={active.title}
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
        </div>
      </div>

      {showVideos && (
        <div className="w-full shrink-0 lg:w-50">
          <p className="mb-2 text-center text-sm font-bold">
            جلسات کلاس آمادگی
          </p>

          <div className="flex items-center justify-center gap-1 overflow-x-auto pb-1 lg:max-h-105 lg:flex-col lg:overflow-y-auto lg:overflow-x-hidden">
            {allVideos.map((video) => (
              <button
                key={video.embed}
                type="button"
                onClick={() => setActiveEmbed(video.embed)}
                className="overflow-hidden rounded-lg border border-white/20"
              >
                <iframe
                  src={video.embed}
                  title={video.title}
                  className="pointer-events-none aspect-video w-full"
                  tabIndex={-1}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function QuizOptionRow({
  option,
  selected,
  disabled,
  onSelect,
  className,
}: {
  option: SpefSpefQuiz;
  selected: boolean;
  disabled: boolean;
  onSelect: (id: string) => void;
  className: string;
}) {
  const quizImg =
    option.id === "kangaroo-online" || option.id === "kangaroo-onsite" ? (
      <Image
        alt="simple-kangoro"
        src={kangoroSimple}
        className="sm:w-16 w-10"
      />
    ) : option.id === "bebras-online" ? (
      <Image alt="simple-samour" src={samourSimple} className="sm:w-18 w-10" />
    ) : option.id === "combo-online" || option.id === "combo-onsite" ? (
      <div className="flex flex-row col-span-2 row-span-2 items-center">
        <Image
          alt="simple-kangoro"
          src={kangoroSimple}
          className="sm:w-14 w-10"
        />
        <Plus strokeWidth={3} className="text-blue-600 w-3 shrink-0" />
        <Image
          alt="simple-samour"
          src={samourSimple}
          className="sm:w-16 w-10"
        />
      </div>
    ) : (
      ""
    );

  return (
    <div className="w-full rounded-2xl p-2.5">
      <label
        className={`w-full rounded-2xl flex items-center justify-between gap-3 sm:p-4 p-2 transition-all duration-200 border-2 ${disabled ? "opacity-20 cursor-not-allowed" : "cursor-pointer"} ${selected ? "border-2 border-blue-300 bg-blue-200" : "border-white"} ${selected && !disabled ? "" : !disabled ? "hover:bg-gray-100" : ""} ${className} `}
      >
        <div className="md:flex w-6/7 grid sm:grid-cols-4 grid-cols-2 items-center md:gap-3 gap-1">
          <div className="w-fit sm:ml-5 ml-none sm:mx-0 mx-auto">{quizImg}</div>
          <div className="w-full flex sm:flex-row flex-col sm:justify-between justify-center items-center">
            <span className="col-span-2 md:text-[15px] text-[12px] sm:text-right text-center text-[#89898B] ">
              {option.title}
            </span>

            <div className="col-span-2 col-start-3 flex items-center md:gap-3 gap-1">
              <span className="sm:text-xl text-sm font-normal whitespace-nowrap sm:mt-0 mt-1">
                {(option.price * 1000).toLocaleString("fa-IR")}
                &nbsp;
                <span className="font-normal text-sm">تومان</span>
              </span>
            </div>
          </div>
        </div>
        <input
          type="radio"
          name="quiz-option"
          disabled={disabled}
          checked={selected}
          onChange={() => onSelect(option.id)}
          className="appearance-none sm:w-7 w-5 h-5 sm:h-7 rounded-full sm:border-7 border-5 border-gray-300 checked:border-blue-600 disabled:border-gray-200 disabled:bg-gray-100 cursor-pointer disabled:cursor-not-allowed transition-colors duration-150"
        />
      </label>
    </div>
  );
}

/* step 2 — Registartion Form*/

function RegistrationFormStep({
  onClose,
  onBack,
  grade,
  wantsQuiz,
  wantsClass,
  selectedQuiz,
  selectedProvince,
  selectedCounty,
  availableCounties,
  handleProvinceChange,
  setSelectedCounty,
}: {
  onClose: () => void;
  onBack: () => void;
  grade: string;
  wantsQuiz: boolean;
  wantsClass: boolean;
  selectedQuiz: string;
  selectedProvince: string;
  selectedCounty: string;
  availableCounties: County[];
  handleProvinceChange: (val: string) => void;
  setSelectedCounty: (val: string) => void;
}) {
  const [errors, setErrors] = useState<
    Partial<Record<keyof SignupFormValues, string>>
  >({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { addToCart } = useCart();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const values = Object.fromEntries(formData.entries());

    const result = signupSchema.safeParse(values);

    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      setErrors(
        Object.fromEntries(
          Object.entries(fieldErrors).map(([key, msgs]) => [key, msgs?.[0]])
        )
      );
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    const selectedItems = {
      examType: wantsQuiz ? selectedQuizData?.title : "در آزمون شرکت نمی‌کنم",

      examPreparationClass: wantsClass
        ? "در کلاس شرکت می‌کنم"
        : "در کلاس شرکت نمی‌کنم",

      quiz: wantsQuiz
        ? {
            id: selectedQuizData?.id,
            title: selectedQuizData?.title,
            price: selectedQuizData?.price,
            productId: selectedQuizData?.productId,
          }
        : null,

      class: wantsClass
        ? {
            title: selectedClassData?.title,
            sessions: selectedClassData?.sessions,
            price: selectedClassData?.price,
            productId: selectedClassData?.productId,
          }
        : null,

      totalPrice,
    };

    const response = await submitKangarooRegistration({
      ...result.data,
      selectedItems,
    });

    setIsSubmitting(false);

    if (!response.success) {
      return;
    }

    const gradeData = optionsCQ.grades[grade];
    const quizProductId = wantsQuiz
      ? optionsCQ.quizzes[selectedQuiz as keyof SpefQuiz]?.productId
      : undefined;
    const classProductId = wantsClass
      ? gradeData?.prepClass?.productId
      : undefined;

    const idsToFetch = [quizProductId, classProductId].filter(
      (id): id is string => Boolean(id)
    );

    if (idsToFetch.length > 0) {
      const cart = await getCartData(idsToFetch);
      console.log("fetched cart products:", cart.product);
      for (const product of cart.product) {
        addToCart({
          id: product.id,
          price: product.price,
          discountAmount: product.discountAmount,
          name: product.name,
          description: product.description,
          thumbnailUrl: product.thumbnailUrl,
        });
      }
      window.location.href = APP_ROUTES.BASKET;
    }
  };

  const gradeData = optionsCQ.grades[grade];

  const selectedQuizData = wantsQuiz
    ? optionsCQ.quizzes[selectedQuiz as keyof SpefQuiz]
    : undefined;

  const selectedClassData = wantsClass ? gradeData?.prepClass : undefined;

  const totalPrice =
    (selectedQuizData?.price ?? 0) + (selectedClassData?.price ?? 0);

  return (
    <>
      <div className="mb-2 flex items-center justify-between sm:p-5 p-3">
        <X
          onClick={onClose}
          aria-label="Close"
          size={25}
          className="text-gray-600 hover:text-black cursor-pointer transition-colors duration-200"
        />
        <h1 className="font-semibold sm:text-2xl text-[18px] text-gray-900">
          فرم ثبت‌نام آزمون کانگورو
        </h1>
      </div>
      <h4
        style={{ direction: "rtl" }}
        className="flex font-light text-black/50 gap-2.5 items-center sm:px-7 px-2 mb-10 sm:text-md text-sm"
      >
        <Info />
        لطفا اطلاعات زیر را تکمیل کنید.
      </h4>
      <form
        id="regForm"
        action=""
        method="POST"
        onSubmit={handleSubmit}
        className="sm:text-md text-sm"
      >
        <div
          style={{ direction: "rtl" }}
          className="flex sm:flex-row flex-col justify-between sm:mx-10 mx-2 my-5 gap-4"
        >
          <div className="flex flex-col flex-1 space-y-3">
            <label htmlFor="fName">نام</label>
            <input
              type="text"
              name="fName"
              id="fName"
              placeholder="نام خود را وارد کنید"
              className="border border-LightGray rounded-xl p-3
              focus:outline-none
              focus:border-blue-400
              focus:ring-2
              focus:ring-blue-50
              focus:shadow-[0_0_6px_rgba(59,130,246,0.10)]
              transition-all duration-200"
            />
            {errors.fName && (
              <span className="text-red-500 text-sm">{errors.fName}</span>
            )}
          </div>
          <div className="flex flex-col flex-1 space-y-3">
            <label htmlFor="lName">نام خانوادگی</label>
            <input
              type="text"
              name="lName"
              id="lName"
              placeholder="نام خانوادگی خود را وارد کنید"
              className="border border-LightGray rounded-xl p-3
              focus:outline-none
              focus:border-blue-400
              focus:ring-2
              focus:ring-blue-50
              focus:shadow-[0_0_6px_rgba(59,130,246,0.10)]
              transition-all duration-200"
            />
            {errors.lName && (
              <span className="text-red-500 text-sm">{errors.lName}</span>
            )}
          </div>
        </div>
        <div
          style={{ direction: "rtl" }}
          className="flex sm:flex-row flex-col justify-between sm:mx-10 mx-2 my-5 gap-4"
        >
          <div className="flex flex-col flex-1 space-y-3">
            <label htmlFor="phNum">شماره تماس</label>
            <input
              type="text"
              name="phNum"
              id="phNum"
              placeholder="شماره خود را وارد کنید"
              className="border border-LightGray rounded-xl p-3
              focus:outline-none
              focus:border-blue-400
              focus:ring-2
              focus:ring-blue-50
              focus:shadow-[0_0_6px_rgba(59,130,246,0.10)]
              transition-all duration-200"
            />
            {errors.phNum && (
              <span className="text-red-500 text-sm">{errors.phNum}</span>
            )}
          </div>
          <div className="flex flex-col flex-1 space-y-3">
            <label htmlFor="grade">مقطع تحصیلی</label>
            <SelectField
              id="grade"
              placeholder="انتخاب کنید"
              value={grade}
              disabled
              options={gradeOptions}
              className="w-full p-3 rounded-xl border border-LightGray bg-white px-4 focus:outline-0 text-black text-right invalid:text-gray-500 cursor-pointer disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
            />
            <input type="hidden" name="grade" value={grade} />
            {errors.grade && (
              <span className="text-red-500 text-sm">{errors.grade}</span>
            )}
          </div>
        </div>
        <div
          style={{ direction: "rtl" }}
          className="flex sm:flex-row flex-col justify-between sm:mx-10 mx-2 my-5 gap-4"
        >
          <div className="flex flex-col flex-1 space-y-3">
            <label htmlFor="natCode">کدملی / کد پاسپورت</label>
            <input
              type="text"
              name="natCode"
              id="natCode"
              placeholder="کدملی یا کد پاسپورت خود را وارد کنید"
              className="border border-LightGray rounded-xl p-3
              focus:outline-none
              focus:border-blue-400
              focus:ring-2
              focus:ring-blue-50
              focus:shadow-[0_0_6px_rgba(59,130,246,0.10)]
              transition-all duration-200"
            />
            {errors.natCode && (
              <span className="text-red-500 text-sm">{errors.natCode}</span>
            )}
          </div>
          <div className="flex flex-col flex-1 space-y-3">
            <label htmlFor="lName">تابعیت</label>
            <div className="flex flex-row justify-between gap-2">
              <label
                htmlFor="iran"
                className="border border-LightGray rounded-xl p-3 flex-1 w-full items-center flex cursor-pointer"
              >
                <input
                  type="radio"
                  value="iran"
                  id="iran"
                  name="nation"
                  className="appearance-none w-5 h-5 rounded-full border-5 border-gray-300 checked:border-blue-600 disabled:border-gray-200 disabled:bg-gray-100 cursor-pointer disabled:cursor-not-allowed transition-colors duration-150 ml-5"
                />
                ایرانی
              </label>
              <label
                htmlFor="nonIran"
                className="border border-LightGray rounded-xl p-1.5 flex-1 w-full items-center flex cursor-pointer"
              >
                <input
                  type="radio"
                  value="nonIran"
                  id="nonIran"
                  name="nation"
                  className="appearance-none w-5 h-5 rounded-full border-5 border-gray-300 checked:border-blue-600 disabled:border-gray-200 disabled:bg-gray-100 cursor-pointer disabled:cursor-not-allowed transition-colors duration-150 ml-5"
                />
                غیر ایرانی
              </label>
            </div>
            {errors.nation && (
              <span className="text-red-500 text-sm">{errors.nation}</span>
            )}
          </div>
        </div>
        <div
          style={{ direction: "rtl" }}
          className="flex sm:flex-row flex-col justify-between sm:mx-10 mx-2 my-5 gap-4"
        >
          <div className="flex flex-col flex-1 space-y-3">
            <label htmlFor="province">استان</label>
            <SelectField
              id="province"
              placeholder="انتخاب کنید"
              options={provinces.map((p) => ({
                id: String(p.id),
                name: p.name,
              }))}
              value={selectedProvince}
              onChange={handleProvinceChange}
              className="w-full p-3 rounded-xl border border-LightGray bg-white px-4 focus:outline-0 text-black text-right invalid:text-gray-500 cursor-pointer disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
            />

            <input type="hidden" name="province" value={selectedProvince} />
            {errors.province && (
              <span className="text-red-500 text-sm">{errors.province}</span>
            )}
          </div>
          <div className="flex flex-col flex-1 space-y-3">
            <label htmlFor="grade">شهرستان</label>
            <SelectField
              id="county"
              placeholder={
                selectedProvince
                  ? "شهرستان خود را انتخاب کنید"
                  : "ابتدا استان را انتخاب کنید"
              }
              options={availableCounties.map((c) => ({
                id: String(c.id),
                name: c.name,
              }))}
              value={selectedCounty}
              onChange={setSelectedCounty}
              disabled={!selectedProvince}
              className="w-full p-3 rounded-xl border border-LightGray bg-white px-4 focus:outline-0 text-black text-right invalid:text-gray-500 cursor-pointer disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
            />

            <input type="hidden" name="county" value={selectedCounty} />
            {errors.county && (
              <span className="text-red-500 text-sm">{errors.county}</span>
            )}
          </div>
        </div>
        <div
          style={{ direction: "rtl" }}
          className="flex justify-between sm:mx-10 mx-2 my-5 gap-2.5"
        >
          <div className="flex flex-col flex-1 space-y-3">
            <label htmlFor="address">آدرس محل سکونت</label>
            <textarea
              name="address"
              id="address"
              rows={3}
              placeholder="آدرس محل سکونت خود را وارد کنید"
              className="border border-LightGray rounded-xl p-3
              focus:outline-none
              focus:border-blue-400
              focus:ring-2
              focus:ring-blue-50
              focus:shadow-[0_0_6px_rgba(59,130,246,0.10)]
              transition-all duration-200"
            />
            {errors.address && (
              <span className="text-red-500 text-sm">{errors.address}</span>
            )}
          </div>
        </div>
        <input type="hidden" name="quiz" value={selectedQuiz} />
      </form>
      <h4
        style={{ direction: "rtl" }}
        className="flex text-xs text-black/50 gap-2.5 items-center px-8"
      >
        <AlertCircle size={20} />
        پس از تکمیل فرم، اطلاعات شما برای بررسی و ثبت نهایی دریافت می‌شود.
      </h4>
      <div className="w-1/3 flex gap-3 mt-5">
        <button
          onClick={onBack}
          className="border flex-1 sm:text-[17px]
        text-sm border-red-500 text-red-500 transition-colors duration-300 hover:bg-red-500 hover:text-white rounded-xl cursor-pointer p-2"
        >
          بازگشت
        </button>
        <button
          type="submit"
          form="regForm"
          disabled={isSubmitting}
          className="flex-1 bg-blue-600 text-white rounded-xl hover:bg-blue-800 transition-colors duration-300 cursor-pointer p-2 sm:text-[17px] text-sm"
        >
          {isSubmitting ? "در حال ارسال..." : "ادامه"}
        </button>
      </div>
    </>
  );
}

function SelectField({
  options,
  placeholder,
  value,
  onChange,
  disabled,
  className,
}: {
  id: string;
  options: SelectOptionsType[];
  error?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  className: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.id === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`${className} flex items-center justify-between w-full 
        focus:outline-none 
        focus:border-blue-400 
        focus:ring-2 
        focus:ring-blue-100 
        focus:shadow-[0_0_8px_rgba(59,130,246,0.12)]
      `}
        disabled={disabled}
      >
        <span className="text-right flex-1">
          {selectedOption?.name || placeholder || "انتخاب کنید"}
        </span>
        <ChevronDown
          size={20}
          className={`text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
          <div className="max-h-60 overflow-y-auto">
            {options.map(({ name, id: optionId }) => (
              <div
                key={optionId}
                onClick={() => {
                  onChange?.(optionId);
                  setIsOpen(false);
                }}
                className={`sm:px-4 px-2 py-2 cursor-pointer text-md text-[12px] text-right transition-colors duration-150 ${
                  value === optionId
                    ? "bg-blue-100 text-blue-700"
                    : "hover:bg-blue-50"
                }`}
              >
                {name}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
